import { ChakraProvider } from '@chakra-ui/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { isEnabled } from './utils/flags';
import { initWaq } from './lib/waq';
import { initRealtime } from './lib/realtime';
import { chatKeys } from './lib/queryKeys';
import { subscribeHints } from './lib/queryBus';
import { drainOnce } from './lib/waq/queue';
import { flush as flushMetrics } from './lib/metrics';
import { setupQueryCachePersistence } from './lib/persistence/queryCacheDexie';
import { theme } from './theme/index';
import './styles/markdown.css';
import './styles/prism-light.css';

import App from './App';
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import logger from './utils/logger';
import { initErrorTracking } from './utils/errorTracking';
import './config/env'; // Validate environment at startup (fail-fast)
import { migrateToSecureStorage } from './lib/native/secureStorageService';
import { initAppLifecycle } from './lib/native/appLifecycleService';
import { clearBadgeAndNotifications } from './lib/native/pushNotificationService';

// Type declaration for Capacitor and boot timer on window
/** @type {Window & { Capacitor?: { isNativePlatform?: () => boolean }, __bootTimer?: ReturnType<typeof setTimeout> | null }} */
const typedWindow = window;

// Initialize global error tracking (window.onerror, unhandledrejection)
initErrorTracking();

// CRITICAL: Visual debug status for blank screen diagnosis
/** @param {string} status */
const updateBootStatus = (status) => {
  console.log(`[BOOT] ${status}`);
  const el = document.getElementById('boot-status');
  if (el) el.innerText = status;
};

window.onerror = (msg, url, line, col, error) => {
  const errorMsg = `JS Error: ${msg} at ${line}:${col}`;
  updateBootStatus(errorMsg);
  alert(errorMsg);
  return false;
};

window.onunhandledrejection = (event) => {
  const errorMsg = `Unhandled Promise: ${event.reason}`;
  updateBootStatus(errorMsg);
  alert(errorMsg);
};

/**
 * Typed window alias to satisfy TS-in-JS for optional flags set elsewhere
 * @typedef {Window & { timegenieUpdatePromptShown?: boolean; currentUser?: { id?: string } }} AppWindow
 */
/** @type {AppWindow} */
const w = window;

// Narrow root element nullability before creating root
const container = document.getElementById('root');
if (!container) {
  throw new Error('Root element with id "root" not found');
}
const root = createRoot(container);

// Initialize a shared QueryClient for TanStack Query.
// - Queries: local-first with background revalidation on focus/reconnect
// - Mutations: light retry policy (tuned later when WAQ lands)
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Keep data fresh enough without thrashing network; hydrate from local cache on boot
      staleTime: 60 * 1000, // 1 minute default; lists will override as needed
      gcTime: 30 * 60 * 1000, // 30 minutes to aid offline reuse
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
      retry: 2,
    },
    mutations: {
      retry: 2,
    },
  },
});

async function bootstrap() {
  updateBootStatus('Starting Bootstrap...');
  logger.info('🚀 Bootstrap: Starting...');

  // Timeout helper for critical async tasks
  /**
   * @param {Promise<any>} promise
   * @param {number} timeoutMs
   * @param {string} taskName
   */
  const withTimeout = (promise, timeoutMs, taskName) => {
    return Promise.race([
      promise,
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error(`Timeout: ${taskName} took longer than ${timeoutMs}ms`)),
          timeoutMs,
        ),
      ),
    ]);
  };

  // Migrate auth tokens to secure storage on iOS (one-time migration)
  try {
    updateBootStatus('Migrating Storage...');
    logger.info('🚀 Bootstrap: Migrating to secure storage...');
    await withTimeout(migrateToSecureStorage(), 3000, 'migrateToSecureStorage');
    logger.info('🚀 Bootstrap: Migration complete');
  } catch (e) {
    logger.warn('🚀 Bootstrap: Migration failed or timed out (non-blocking)', e);
  }

  // Initialize app lifecycle handlers for iOS
  try {
    updateBootStatus('Initializing Lifecycle...');
    logger.info('🚀 Bootstrap: Initializing app lifecycle...');
    await withTimeout(
      initAppLifecycle({
        onForeground: () => {
          logger.info('App foregrounded - refreshing data');
          clearBadgeAndNotifications();
        },
        onBackground: () => {
          logger.info('App backgrounded');
        },
        onUrlOpen: (url) => {
          logger.info('Deep link opened:', url);
          if (url && typeof window !== 'undefined') {
            try {
              // Handle both universal links (https://...) and custom schemes (timegenie://...)
              let targetPath = '';
              if (url.startsWith('timegenie://')) {
                // Strip the scheme and treat the rest as the path
                targetPath = '/' + url.replace('timegenie://', '');
              } else {
                const parsed = new URL(url);
                targetPath = parsed.pathname + parsed.search + parsed.hash;
              }

              if (targetPath) {
                logger.info('Routing deep link to:', targetPath);
                window.location.href = targetPath;
              }
            } catch (err) {
              logger.warn('Failed to parse deep link URL:', url, err);
            }
          }
        },
      }),
      3000,
      'initAppLifecycle',
    );
    logger.info('🚀 Bootstrap: Lifecycle initialized');
  } catch (e) {
    logger.warn('🚀 Bootstrap: Lifecycle init failed or timed out (non-blocking)', e);
  }

  // Initialize Write-Ahead Queue (WAQ) behind feature flag
  try {
    if (isEnabled('FF_SYNC_V2')) {
      updateBootStatus('Initializing WAQ...');
      logger.info('🚀 Bootstrap: FF_SYNC_V2 enabled, initializing WAQ...');
      initWaq();
      initRealtime(queryClient);
      logger.info('🚀 Bootstrap: WAQ initialized');
    }
  } catch (e) {
    logger.warn('🚀 Bootstrap: WAQ init failed (non-blocking)', e);
  }

  try {
    updateBootStatus('Setting up Cache...');
    logger.info('🚀 Bootstrap: Setting up query cache persistence...');
    await withTimeout(setupQueryCachePersistence(queryClient), 3000, 'setupQueryCachePersistence');
    logger.info('🚀 Bootstrap: Cache persistence ready');
  } catch (error) {
    logger.warn(
      '🚀 Bootstrap: Query cache persistence disabled or timed out (non-blocking):',
      error,
    );
  }

  updateBootStatus('Rendering Root...');
  logger.info('🚀 Bootstrap: Rendering root...');
  root.render(
    // Temporarily disable StrictMode to debug session issues
    // <React.StrictMode>
    <>
      <QueryClientProvider client={queryClient}>
        <ChakraProvider theme={theme}>
          <BrowserRouter>
            <App />
          </BrowserRouter>
        </ChakraProvider>
      </QueryClientProvider>
    </>,
    // </React.StrictMode>,
  );

  // Hide the boot status indicator and cancel the boot timeout after React renders
  // Use requestAnimationFrame to ensure DOM is painted first
  requestAnimationFrame(() => {
    const bootStatus = document.getElementById('boot-status');
    if (bootStatus) {
      bootStatus.style.display = 'none';
    }
    // Cancel the boot timeout safety net defined in index.html
    if (typedWindow.__bootTimer) {
      clearTimeout(typedWindow.__bootTimer);
      typedWindow.__bootTimer = null;
    }
  });

  // Register the service worker for PWA and push notifications with enhanced error handling
  // Skip registration on native platforms (Capacitor) as it conflicts with native push notifications

  // Check if we're running in a native Capacitor environment
  const isNativePlatform = !!(
    typedWindow.Capacitor &&
    typedWindow.Capacitor.isNativePlatform &&
    typedWindow.Capacitor.isNativePlatform()
  );

  // Listen for service worker messages (web only)
  if (!isNativePlatform && 'serviceWorker' in navigator) {
    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data && event.data.type === 'SW_ACTIVATED') {
        logger.info('🔄 Service worker activated:', event.data.version);
        // Don't automatically reload - let user-initiated updates handle this
      } else if (event.data && event.data.type === 'RELOAD_PAGE') {
        // Legacy support for explicit reload requests
        w.location.reload();
      } else if (event.data && event.data.type === 'PUSH_NOTIFICATION_CLICK') {
        // Handle push notification click navigation.
        // The service worker sends this when user taps a notification.
        const targetUrl = event.data?.url || '/';
        const notificationData = event.data?.data || {};
        logger.info('🔔 Push notification clicked, navigating to:', targetUrl, notificationData);
        try {
          // Parse the URL to get the pathname and search params
          const url = new URL(targetUrl, w.location.origin);

          // Check if this is a chat session deep link
          // URL format: /chat?session=<session_id> or notification data contains session_id
          const sessionId = url.searchParams.get('session') || notificationData.session_id;

          if (sessionId && (url.pathname === '/chat' || url.pathname.startsWith('/chat'))) {
            // Check if we're already on /chat
            if (w.location.pathname === '/chat' || w.location.pathname.startsWith('/chat')) {
              // Already on chat tab - just dispatch the event to select the session
              w.dispatchEvent(
                new CustomEvent('timegenie:chat-select', {
                  detail: { sessionId: sessionId },
                }),
              );
              logger.info('🎯 Already on chat, dispatched chat-select for session:', sessionId);
            } else {
              // Navigate to chat WITH the session param - useChatManager will read it on mount
              w.location.href = `/chat?session=${sessionId}`;
              logger.info('🎯 Navigating to chat with session param:', sessionId);
            }
          } else if (url.origin === w.location.origin) {
            // Same origin: navigate within the app
            w.location.href = url.pathname + url.search;
          } else {
            // Different origin (shouldn't happen, but fallback)
            w.location.href = targetUrl;
          }
        } catch (err) {
          logger.error('❌ Failed to navigate from push notification:', err);
          // Fallback: just go to chat
          w.location.href = '/chat';
        }
      } else if (event.data && event.data.type === 'new_message') {
        // Bridge from SW push -> UI. When a push arrives, refresh chats so the new message shows.
        // We read user id from window.currentUser (set in AuthContext on sign-in) to avoid importing context here.
        const uid = w.currentUser?.id;
        logger.info('📨 SW message: new_message received; triggering refresh', {
          hasUser: !!uid,
          payload: event.data?.payload,
          source: event.data?.source,
        });
        if (uid) {
          // Under FF_SYNC_V2, rely on realtime and perform targeted invalidation
          // for the chat sessions list as a light nudge. If legacy sync is active,
          // fall back to coarse refresh.
          try {
            if (isEnabled('FF_SYNC_V2')) {
              try {
                queryClient.invalidateQueries({ queryKey: chatKeys.sessions(uid) });
              } catch (_) {
                // ignore
              }
            } else {
              // Legacy path removed: rely on realtime/invalidations under FF_SYNC_V2
            }
          } catch (_) {
            // If flag check fails unexpectedly, default to legacy path
            // Legacy fallback removed
          }
        }
      }
    });
  }

  try {
    if (!isNativePlatform && process.env.NODE_ENV === 'production') {
      serviceWorkerRegistration.register({
        onSuccess: (registration) => {
          logger.info('✅ Service worker registered successfully');
        },
        onUpdate: (registration) => {
          logger.info('🔄 New service worker available');

          if (w.timegenieUpdatePromptShown) {
            logger.info('Update prompt already shown, skipping');
            return;
          }

          w.timegenieUpdatePromptShown = true;

          if (w.confirm('A new version of TimeGenie is available. Refresh to update?')) {
            const reg = registration;
            if (reg && reg.waiting) {
              reg.waiting.postMessage({ type: 'SKIP_WAITING' });
            }
            setTimeout(() => {
              w.location.reload();
            }, 100);
          } else {
            setTimeout(() => {
              w.timegenieUpdatePromptShown = false;
            }, 30000);
          }
        },
      });
    } else {
      // In development or native, ensure any existing SW is unregistered to avoid cached chunks
      if (!isNativePlatform) {
        serviceWorkerRegistration.unregister();
      }
    }
  } catch (error) {
    logger.error('❌ Service worker setup failed:', error);
  }
}

void bootstrap();
